import { CostModelConfig } from './cost-model';
import { ExtrusionPlaneGenerationMode, ManufacturingMethod, PartType, ProjectType, RunType } from './enums';
import { LoadCaseItem } from './loadcase.model';
import { Material } from './material.model';
import type { ManufacturingCase, RunSummary } from '@infinitform/platform-core';
import { Group } from 'three';

export type { ManufacturingCase, RunSummary };

export interface SelectorObject {
  data: { id: string; faces: { id: string; name: string }[]; wires: string[]; name?: string };
  group: Group;
  type: PartType;
  faces: string[];
  wires: string[];
}

export interface ProjectGoalResponse {
  id: string;
  type: string;
  params?: any;
}

export interface ProjectGoalContraint {
  id: string;
  type?: string;
  responseId?: string;
  constraintType?: 'Von Mises' | 'Tsai Hill';
  lowerBound?: number;
  upperBound?: number;

  lowerBoundXX?: number;
  lowerBoundYY?: number;
  lowerBoundZZ?: number;
  upperBoundXX?: number;
  upperBoundYY?: number;
  upperBoundZZ?: number;

  params?: any;
}

export interface ProjectGoalObjective {
  type: 'Min' | 'Max';
  responseId:
    | 'compliance-response'
    | 'weighted-compliance-response'
    | 'mass-response'
    | 'vibration-response'
    | 'frequency'
    | 'mass-rms-response'
    | 'stress-rms-response';
  volumeWeight?: number;
  complianceWeight?: number;
  stressWeight?: number;
}

export interface AccelerationItem {
  accelerationDirectionVector: number[];
  accelerationGravity: number;
}

export interface FrequencyTableEntry {
  frequency: number;
  value: number;
}

export const DEFAULT_SOFT_JAWS_CONFIG: SoftJawsConfig = {
  length: 80,
  width: 40,
  height: 30,
  jawGap: 30,
  cutterDiameter: 4,
  modelRotation: { x: 0, y: 0, z: 0 },
  modelOrigin: { x: 0, y: 0, z: 0 },
  partOffset: { x: 0, y: 0, z: -20 }, // z=-20 ≙ legacy clampingDepth=20 (part clamped 20 mm deep)
  edited: false,
  autoOriented: false,
  reliefs: { enabled: true, minAngleDeg: 20 },
};

export interface SoftJawsConfig {
  length: number;
  width: number;
  height: number;
  /** Inner distance between the two jaw faces (mm). */
  jawGap: number;
  cutterDiameter: number;
  modelRotation: { x: number; y: number; z: number };
  /** Rotation pivot — never a translation. Passed to FreeCAD as the rotation center. */
  modelOrigin: { x: number; y: number; z: number };
  /**
   * Part translation in mm, world-Z up:
   *   z = 0  → part lowest vertex on jaw top
   *   z < 0  → part sinks into the jaws (legacy clampingDepth=20 ⇔ z=-20)
   *   z > 0  → part lifted above the jaws
   */
  partOffset: { x: number; y: number; z: number };
  edited: boolean;
  /**
   * SJD-29: set once after the first open has evaluated part orientation, so the
   * auto-rotate-longest-axis-to-Y pass never runs again and can't override a part
   * the user has since re-oriented. Absent on legacy projects (treated as false).
   */
  autoOriented?: boolean;
  reliefs?: { enabled: boolean; minAngleDeg: number };
  /** @deprecated legacy field. */
  jawWall?: number;
  /** @deprecated legacy field. */
  clampingDepth?: number;
}

export interface GuideCurve {
  id: string;
  name: string;
  points: number[][]; // Array of [x, y, z] control points
  closed: boolean; // Whether it's a closed loop
  smooth: boolean; // Whether to use smooth spline interpolation
  source?: 'manual' | 'model_edge'; // How the curve was created
  color?: string; // Optional custom color (hex)
  locked?: boolean; // Whether the curve is locked from editing
}

export type ShapeGeometryType = 'polygon' | 'circle' | 'ring';

export interface CircleGeometryParams {
  center: [number, number, number]; // [x, y, z]
  radius: number;
}

export interface RingGeometryParams {
  center: [number, number, number]; // [x, y, z]
  outerRadius: number;
  innerRadius: number;
}

export interface GuideShape {
  id: string;
  name: string;
  points: number[][]; // Array of [x, y, z] vertices (must form closed polygon)
  holes?: number[][][]; // Array of hole polygons, each hole is array of [x, y, z] vertices
  extrude_height: number; // Height to extrude the shape
  color?: string; // Optional custom color (hex)
  locked?: boolean; // When true, shape cannot be modified or deleted
  // Geometry type for CAD export (circles/rings export as true arcs, not polygons)
  geometryType?: ShapeGeometryType;
  geometryParams?: CircleGeometryParams | RingGeometryParams;
  // CAD data for browser-generated parts
  cadData?: {
    stepBlob?: Blob; // Browser-generated STEP file
    partId?: string; // Backend part ID after save
    sourceType: 'sketch' | 'primitive' | 'imported';
  };
}

/**
 * CAD Part created in browser using OpenCascade.js
 * Stored at project level for persistence
 */
export interface CADPart {
  id: string;
  name: string;
  color: string;
  stepUrl?: string; // S3 URL of STEP file
  plyUrl?: string; // S3 URL of PLY mesh
  sourceShapeId?: string; // Original GuideShape ID if created from shape
  createdAt: number;
  updatedAt: number;
}

// Predefined curve colors for visual distinction
export const CURVE_COLORS = [
  '#10b981', // Emerald
  '#3b82f6', // Blue
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#14b8a6', // Teal
  '#f97316' // Orange
];

// Predefined shape colors for visual distinction
export const SHAPE_COLORS = [
  '#6366f1', // Indigo
  '#a855f7', // Purple
  '#06b6d4', // Cyan
  '#84cc16', // Lime
  '#f43f5e', // Rose
  '#0ea5e9', // Sky
  '#d946ef', // Fuchsia
  '#22c55e' // Green
];

export const DEFAULT_MOLD_CONFIG: MoldConfig = {
  partTypes: {},
  open_dir: [0, 0, 1],
  mold: {
    wall_thickness: 1,
    settings_hallowed: {
      use_provided_design_domain: false
    },
    settings_design_domain: {
      faces_to_pierce: {
        type: 'angle',
        max_angle_deg: 75
      },
      tapering: {
        enabled: true,
        angle_deg: 2
      },
      fillets: {
        enabled: true,
        radius: 3,
        min_crease_angle_deg: 5
      }
    }
  },
  ribs: {
    manufacturing: {
      min_feature_size: 3
    },
    grids: []
  },
  bosses: {
    common: {
      r: 10,
      wall_thickness: 1
    },
    list: []
  }
};

export interface MoldConfig {
  partTypes: { [partId: string]: 'design_domain_mold' | 'hallowed_mold' };
  open_dir: number[];
  mold: {
    wall_thickness: number;
    settings_hallowed: {
      use_provided_design_domain: boolean;
    };
    settings_design_domain: {
      faces_to_pierce: {
        type: 'angle';
        max_angle_deg: number;
      };
      tapering: {
        enabled: boolean;
        angle_deg: number;
      };
      fillets: {
        enabled: boolean;
        radius: number;
        min_crease_angle_deg: number;
      };
    };
  };
  ribs: {
    manufacturing: {
      min_feature_size: number;
    };
    grids: RibGrid[];
  };

  bosses: {
    common: {
      r: number;
      wall_thickness: number;
    };
    list: {
      pos: number[];
      r: number;
      wall_thickness: number;
      color?: string; // Optional custom color (hex)
    }[];
  };
}

// Rib grid configuration
export interface RibGrid {
  id: string; // Unique identifier for the grid
  name: string; // Display name for the grid
  type: 'guide_curve';
  space1: number;
  space2: number;
  thickness: number;
  guide_curves: GuideCurve[];
  guide_shapes: GuideShape[];
}

// Predefined boss colors for visual distinction
export const BOSS_COLORS = [
  '#3b82f6', // Blue
  '#ef4444', // Red
  '#f59e0b', // Amber
  '#10b981', // Emerald
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#14b8a6', // Teal
  '#f97316' // Orange
];

export interface Project {
  name: string;
  materials: Material[];
  status: number;
  optimization?: {
    additive_design: string;
    additive_design_parametric: string;
    loadcases: { id: string; t: string; x: string; y: string; z: string; s: string; attributes: string }[];
    reaction_force?: string;
    v2?: {
      organic: {
        geometryUrl: string;
        loadcases: { id: string; t: string; x: string; y: string; z: string; s?: string }[];
      };
      prismatic: {
        geometryUrl: string;
        loadcases: { id: string; t: string; x: string; y: string; z: string; s?: string }[];
      };
      frequenciesExists: boolean;
      frequencies: any;
      eigenFrequenciesExists?: boolean;
      eigenFrequencies?: string;
      freqResponseExists?: boolean;
      freqResponseModalStress?: string;
      manufacturingCostExists?: boolean;
      manufacturingCost?: string;
    };
  };
  parts: {
    design: string[];
    nondesign: string[];
  };
  partNames?: { [partId: string]: string }; // Custom part name mappings
  partMaterials?: { [partId: string]: string }; // Maps partId to materialId
  partTypes?: { [partId: string]: 'design_domain_mold' | 'hallowed_mold' }; // Maps partId to part type
  partVisibility?: { [partId: string]: boolean }; // Maps partId to visibility state
  loadcases: LoadCaseItem[];
  goal: {
    type: string;
    objective: ProjectGoalObjective;
    fractionOfMaterial: number;
    massTarget: number;
    frequencyConstraint: number;
    constraints: ProjectGoalContraint[];
    responses: ProjectGoalResponse[];
    /**
     * Geometry volumes in mm³, captured from the engine on every save.
     * fractionOfMaterial is a design-space percentage, but the optimizer's
     * volume fraction spans the whole domain — XML generation needs these
     * to convert. Absent on projects saved before CORE-42.
     */
    designVolume?: number;
    nonDesignVolume?: number;
  };
  resolution: 'Low' | 'Medium' | 'High';
  userDefinedResolution?: {
    enabled: boolean;
    value: number;
  };
  solverParameters?: {
    static: { tolerance: number; maxIterations: number };
    eigenvalue: { tolerance: number; maxIterations: number };
  };
  symmetry: {
    xy: boolean;
    xz: boolean;
    yz: boolean;
  };
  manufacturingMethod: ManufacturingMethod;
  millingVector: {
    xp: boolean;
    yp: boolean;
    zp: boolean;
    xm: boolean;
    ym: boolean;
    zm: boolean;
  };
  drawDirectionVector: {
    xp: boolean;
    yp: boolean;
    zp: boolean;
    xm: boolean;
    ym: boolean;
    zm: boolean;
  };
  ribsDensity: 'Low' | 'Medium';
  wallThickness: number | 'auto';
  bottomCutFrac: number;
  minimumFeatureSize: number;
  minimumToolSize: number;
  minimumToolSizeUnit: 'mm' | 'in';
  userCoordinateSystem: {
    origin: number[];
    rotation: number[];
    enabled: boolean;
  };
  generateOrganic: boolean;
  generatePrismatic: boolean;
  skeletonMethod: 'Skeleton3D' | 'Skeleton2D';
  stressConstraintEnabled: boolean;
  deformationConstraintEnabled: boolean;
  /**
   * Constraint family for the dynamic (Harmonic Excitation / Random Vibration) objective.
   * Mutually exclusive in the optimizer, so the dynamic objective picks exactly one.
   * Reuses deformationGlobal / maximumDeformationMagnitude / deformationPoints for the
   * active family — only the unit differs (displacement = mm, acceleration = G).
   * Defaults to 'stress' (legacy behaviour).
   */
  dynamicConstraintType?: 'stress' | 'displacement' | 'acceleration';
  flowVersion?: number;

  runType: RunType;
  projectType?: ProjectType;
  softJaws?: SoftJawsConfig;
  mold?: MoldConfig;

  // CAD parts created in browser
  cadParts?: CADPartData[];
  // Sketches created in browser
  sketches?: SketchData[];

  // Cost model for CNC Milling Prismatic
  costModel?: CostModelConfig;

  /**
   * Admin-only knob for CNC Milling Prismatic. Controls the `NumberOfPlanes`
   * XML field sent to the optimizer. Defaults to 2 when undefined.
   */
  numberOfPlanes?: number;

  /**
   * Admin-only knob for CNC Milling Prismatic. Controls the
   * `ExtrusionPlaneGeneration` XML field — `Even`, `GenerateFromDesDomain`,
   * or `GenerateFromInitGuess`. Defaults to `Even` when undefined.
   */
  extrusionPlaneGeneration?: ExtrusionPlaneGenerationMode;

  // Example project flag
  isExample?: boolean;

  unitSystem?: 'metric' | 'imperial';
  /** Per-project density unit override (only meaningful when unitSystem === 'metric'). */
  densityUnit?: 'kg/m³' | 'ton/mm³';
  /** Per-project mass unit override (only meaningful when unitSystem === 'metric'). */
  massUnit?: 'kg' | 'g';

  // --- Multi-manufacturing-method fields ---
  /** List of manufacturing cases. When present and length >= 1 the project is new-shape. */
  manufacturingMethods?: ManufacturingCase[];
  /** Set on child projects. Absent on masters and legacy projects. */
  masterProjectId?: string;
  /** Set on child projects. The UUID linking to master.manufacturingMethods[].methodId. */
  methodId?: string;
  /** Set on master projects. Ordered by manufacturingMethods array order. */
  childProjectIds?: string[];
  /** Set on master projects. Computed by the backend/desktop aggregation hook. */
  runSummary?: RunSummary | null;
}

/**
 * Serializable CAD part data for persistence
 */
export interface CADPartData {
  id: string;
  name: string;
  color: string;
  // Transform data
  position: [number, number, number];
  rotation: [number, number, number, number]; // Quaternion
  scale: [number, number, number];
  // Source data (for regeneration)
  sourceType: 'primitive' | 'extrusion' | 'brep' | 'boolean' | 'imported';
  isDirectEdit?: boolean;
  primitiveParams?: {
    type: 'box' | 'cylinder' | 'sphere' | 'ring';
    dimensions: number[];
  };
  extrudeParams?: {
    points: number[][];
    height: number;
    sketchPlane?: 'XY' | 'XZ' | 'YZ' | 'custom';
    planeOffset?: number;
  };
  brepData?: string;
  // Creation metadata
  createdAt: number;
  updatedAt: number;
}

/**
 * Serializable sketch data for persistence
 */
export interface SketchData {
  id: string;
  name: string;
  plane: 'XY' | 'XZ' | 'YZ' | 'custom';
  planeOffset?: number;
  planeOrigin?: [number, number, number];
  planeNormal?: [number, number, number];
  planeXAxis?: [number, number, number];
  planeYAxis?: [number, number, number];
  referenceFaceId?: string;
  referenceBodyId?: string;
  entities: any[]; // Serialized sketch entities
  isVisible?: boolean;
}
